import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Form, Formik, Field, ErrorMessage } from 'formik';
import classNames from 'classnames';
import { TranslateButton } from 'components/common/translate_button';
import { logError } from 'helpers/errors/bug-report';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/react-hooks';
import { ADD_SECTION, UPDATE_SECTION } from 'graphql/mutations/vip';
import { camelizeKeys } from 'humps';
import { GET_VIP_PROMOS } from 'graphql/queries/vip-promos';
import * as Yup from 'yup';
import { ButtonSpinner } from 'components/common/button_spinner';
import { useTranslation } from 'react-i18next';
import FontAwesomeIconSelectInput from 'components/common/font-awesome-icon-select-input';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  initialValues: VipSectionCreate | VipSectionUpdate;
  title: string;
}

const ModalSection: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { isOpen, closeModal, initialValues, title } = props;

  const [addSection, { loading: isAddSubmitting }] = useMutation(ADD_SECTION, {
    onCompleted: () => {
      toast.success(t('vip.section.add.success'));
      closeModal();
    },
    refetchQueries: ['GetVipPromos', { query: GET_VIP_PROMOS }],
    onError: (err: Error) => {
      logError(err, 'ADD_SECTION', ModalSection.displayName);
      toast.error(t('vip.section.add.failure'));
    },
  });

  const [updateSection, { loading: isUpdateSubmitting }] = useMutation(UPDATE_SECTION, {
    onCompleted: () => {
      toast.success(t('vip.section.edit.success'));
      closeModal();
    },
    refetchQueries: ['GetVipPromos', { query: GET_VIP_PROMOS }],
    onError: (err: Error) => {
      logError(err, 'UPDATE_SECTION', ModalSection.displayName);
      toast.error(t('vip.section.edit.failure'));
    },
  });

  const isSubmitting = isAddSubmitting || isUpdateSubmitting;

  const sectionValidationSchema = Yup.object().shape({
    channel: Yup.string()
      .oneOf(['DANCER', 'CLUB', 'BOTH'])
      .required(t('errors.validation.required', { field_name: t('vip.channel') })),
    name: Yup.object().shape({
      en: Yup.string().required(t('errors.validation.required', { field_name: t('common.name') })),
      es: Yup.string().required(t('errors.validation.required', { field_name: t('common.name') })),
    }),
    sort_order: Yup.number()
      .integer(t('errors.validation.not_integer'))
      .min(0, t('errors.validation.min', { number: 0 }))
      .max(9999, t('errors.validation.max', { number: 9999 }))
      .required(t('errors.validation.required', { field_name: t('common.sort_order') })),
    icon: Yup.string().required(t('errors.validation.required', { field_name: t('vip.icon') })),
  });

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as='div' className='relative z-10' onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-100'
          leave='ease-in duration-200'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
        </Transition.Child>

        <div className='fixed z-10 inset-0 overflow-y-auto'>
          <div className='flex items-end sm:items-center justify-center min-h-full p-4 text-center sm:p-0'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              enterTo='opacity-100 translate-y-0 sm:scale-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0 sm:scale-100'
              leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
            >
              <Dialog.Panel className='relative bg-gray-900 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-3xl sm sm:w-full sm:p-6'>
                <div>
                  <div className='mt-3 sm:mt-5'>
                    <Dialog.Title as='h3' className='text-lg leading-6 text-center font-medium text-gray-300'>
                      {title}
                    </Dialog.Title>
                    <div className='mt-2'>
                      <div className='mt-5 sm:mt-6'>
                        <Formik
                          validateOnBlur={false}
                          validationSchema={sectionValidationSchema}
                          initialValues={initialValues}
                          onSubmit={(values) => {
                            const camelizedValues = camelizeKeys(values);
                            if (values.hasOwnProperty('id')) {
                              updateSection({ variables: { input: camelizedValues } });
                            } else {
                              addSection({ variables: { input: camelizedValues } });
                            }
                          }}
                        >
                          {({ values, touched, errors, isValid, setFieldValue, setFieldTouched }) => (
                            <Form>
                              <div className='sm:col-span-3 mb-4'>
                                <label htmlFor='channel' className='block text-sm font-medium text-gray-500'>
                                  {t('vip.channel')}
                                </label>
                                <div className='mt-1'>
                                  <Field
                                    as='select'
                                    id='channel'
                                    name='channel'
                                    className={classNames(
                                      'mt-1 block w-full pl-3 pr-10 py-2 text-base text-gray-800 border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md',
                                      { ['border-2  border-red-500']: touched.channel && errors.channel }
                                    )}
                                  >
                                    <option value='DANCER'>{t('vip.dancer')}</option>
                                    <option value='CLUB'>{t('vip.club')}</option>
                                    <option value='BOTH'>{t('vip.both')}</option>
                                  </Field>
                                  <ErrorMessage name='channel' className='text-red-500 text-sm' component='div' />
                                </div>
                              </div>

                              <div className='sm:col-span-3 mb-4'>
                                <label htmlFor='icon' className='block text-sm font-medium text-gray-500'>
                                  {t('vip.icon')}
                                </label>
                                <FontAwesomeIconSelectInput
                                  value={values.icon}
                                  onChange={(value) => {
                                    setFieldValue('icon', value);
                                    setFieldTouched('icon', true, false);
                                  }}
                                  placeholder={t('vip.selectIcon')}
                                />
                                <ErrorMessage name='icon' className='text-red-500 text-sm' component='div' />
                              </div>

                              <div className='bg-gray-800 shadow px-4 py-5 sm:rounded-lg sm:p-6 sm:col-span-3 mb-2'>
                                <div className='md:grid md:grid-cols-3 md:gap-6'>
                                  <div className='sm:col-span-3'>
                                    <h3 className='text-lg font-medium leading-6 text-gray-500'>
                                      {t('vip.section.name')}
                                    </h3>
                                  </div>
                                  <div className='sm:col-span-3'>
                                    <label htmlFor='name.en' className='block text-sm font-medium text-gray-500'>
                                      {t('common.english')}
                                    </label>
                                    <div className='mt-1 flex rounded-md shadow-sm'>
                                      <Field
                                        type='text'
                                        name='name.en'
                                        id='name.en'
                                        className={classNames(
                                          'h-9 px-4 py-2 flex-1 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300',
                                          { ['border-2  border-red-500']: touched.name?.en && errors.name?.en }
                                        )}
                                      />
                                    </div>
                                    <ErrorMessage name='name.en' className='text-red-500 text-sm' component='div' />
                                  </div>
                                  <div className='sm:col-span-3'>
                                    <label htmlFor='name.es' className='block text-sm font-medium text-gray-500'>
                                      {t('common.spanish')}
                                    </label>
                                    <div className='mt-1 flex rounded-md shadow-sm'>
                                      <div className='relative flex items-stretch flex-grow focus-within:z-10'>
                                        <Field
                                          type='text'
                                          name='name.es'
                                          id='name.es'
                                          className={classNames(
                                            'h-9 px-4 py-2 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500 min-w-0 rounded-none rounded-l-md sm:text-sm border-gray-300',
                                            {
                                              ['border-2  border-red-500']: touched.name?.es && errors.name?.es,
                                            }
                                          )}
                                        />
                                      </div>
                                      <TranslateButton
                                        stringOriginal={values.name.en}
                                        langFrom='en'
                                        langTo='es'
                                        flatLeft
                                        onTranslate={(translatedString) => setFieldValue('name.es', translatedString)}
                                      />
                                    </div>
                                    <ErrorMessage name='name.es' className='text-red-500 text-sm' component='div' />
                                  </div>
                                </div>
                              </div>

                              <div className='sm:col-span-3 mb-2'>
                                <label htmlFor='sort_order' className='block text-sm font-medium text-gray-500'>
                                  {t('common.sort_order')}
                                </label>
                                <div className='mt-1 flex rounded-md shadow-sm'>
                                  <Field
                                    type='number'
                                    name='sort_order'
                                    id='sort_order'
                                    className={classNames(
                                      'h-9 px-4 py-2 flex-1 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500 block w-full min-w-0 rounded-md sm:text-sm border-gray-300',
                                      { ['border-2  border-red-500']: touched.sort_order && errors.sort_order }
                                    )}
                                  />
                                </div>
                                <ErrorMessage name='sort_order' className='text-red-500 text-sm' component='div' />
                              </div>

                              <div className='sm:col-span-4 mb-8'>
                                <label htmlFor='is_active' className='block text-sm font-medium text-gray-500'>
                                  {t('common.active')}?
                                </label>
                                <div className='mt-1 flex rounded-md shadow-sm'>
                                  <Field
                                    name='is_active'
                                    id='is_active'
                                    className='flex-1 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500 block w-full min-w-0 rounded-none rounded-r-md sm:text-sm border-gray-300'
                                  >
                                    {() => (
                                      <button
                                        type='button'
                                        onClick={() => setFieldValue('is_active', !values.is_active)}
                                        className={classNames(
                                          values.is_active ? 'bg-indigo-600' : 'bg-gray-200',
                                          'relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                                        )}
                                        role='switch'
                                        aria-checked='false'
                                      >
                                        <span className='sr-only'>{t('common.active')}?</span>
                                        <span
                                          aria-hidden='true'
                                          className={classNames(
                                            values.is_active ? 'translate-x-5' : 'translate-x-0',
                                            'pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200'
                                          )}
                                        ></span>
                                      </button>
                                    )}
                                  </Field>
                                </div>
                              </div>

                              <div className='flex justify-between'>
                                <button
                                  type='button'
                                  className='w-40 rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm'
                                  onClick={() => {
                                    closeModal();
                                  }}
                                >
                                  {t('common.cancel')}
                                </button>
                                <button
                                  type='submit'
                                  disabled={!isValid || isSubmitting}
                                  className={classNames([
                                    isValid ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600',
                                    'w-40 ml-3 inline-flex justify-center py-2 px-4 rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:text-sm',
                                  ])}
                                >
                                  {isSubmitting && <ButtonSpinner />} {t('common.submit')}
                                </button>
                              </div>
                            </Form>
                          )}
                        </Formik>
                      </div>
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

ModalSection.displayName = 'ModalSection';

ModalSection.propTypes = {};

export { ModalSection };
